import { useState, useEffect, useCallback } from 'react'
import type { FC, ChangeEvent } from 'react'
import { toast } from 'react-toastify'
import { Popover, Tooltip } from 'antd'
import { CopyOutlined, PrinterOutlined } from '@ant-design/icons'

import { useAppStore } from '@/store'
import { accountsForTesting, formatMoney, isCordovaApp } from '@/helpers'
import { envs } from '@/envs'
import { apiRequest } from '@/helpers/request'
import { useOnlineStatus } from '@/helpers/hooks'
import {
  clearTopupBalancePendingSession,
  setTopupBalancePendingSession,
} from '@/helpers/payment/topup-balance-pending-session'
import type { T_TopupBalancePendingBaseline } from '@/helpers/payment/topup-balance-pending-session'
import { Drawer, Flex, Input, Button, Heading, Radio } from '@/components'
import type { T_UniRes } from '@/types'
import type { T_MoneyAccountPaymentRequestRes, T_UserBalanceRes } from '@/types/responses'
import { QRCodeCP1251 } from './qr-code-cp1251'
import {
  SC_Content,
  SC_CreditingTime,
  SC_FieldRow,
  SC_FieldLabel,
} from './styled'


interface T_TopUpBalanceDrawerProps {
  onClose: () => void
  isBuySubscription?: boolean
  initialAmount?: number
  onPayFromBalance?: () => Promise<void>
  skipMethodSelection?: boolean
}

enum E_Steps {
  MethodSelection = 1,
  AmountInput = 2,
  PaymentTypeSelection = 3,
  PaymentConfirm = 4,
  DetailsByBankDetails = 5,
  DetailsBySbp = 6,
}

enum E_PaymentMethods {
  BankTransfer = 1,
  Instant = 2,
  InternalAccount = 3,
}

type T_TopupPaymentOption = {
  id: number
  title: string
  image?: string | null
  group?: {
    id: number
    title?: string
  } | null
}

type T_TopupPaymentMethodsRes = {
  external: T_TopupPaymentOption[]
}

const getPaymentOptionSearchText = (option: T_TopupPaymentOption) => (
  `${option.title || ''} ${option.group?.title || ''}`.toLowerCase()
)

const isCardPaymentOption = (option: T_TopupPaymentOption) => (
  /карт|visa|master/.test(getPaymentOptionSearchText(option))
)

const isRequisitesPaymentOption = (option: T_TopupPaymentOption) => (
  /реквизит/.test(getPaymentOptionSearchText(option))
)

const getPaymentCreditingTime = (option: T_TopupPaymentOption) => {
  if (isCardPaymentOption(option)) {
    return { variant: 'fast' as const, text: 'Зачисление ~2 минуты' }
  }

  if (isRequisitesPaymentOption(option)) {
    return { variant: 'slow' as const, text: 'Зачисление ~1 банковский день' }
  }

  return null
}

const sortPaymentOptionsCardsFirst = (options: T_TopupPaymentOption[]) => {
  const rank = (option: T_TopupPaymentOption) => {
    if (isCardPaymentOption(option)) return 0
    if (isRequisitesPaymentOption(option)) return 1
    return 2
  }

  return options
    .map((option, index) => ({ option, index }))
    .sort((a, b) => rank(a.option) - rank(b.option) || a.index - b.index)
    .map(({ option }) => option)
}

type T_TopupAccount = T_UserBalanceRes['list'][number]

const PAYMENT_PROCESS_TYPE = {
  FormGet: 'form-get',
  FormPost: 'form-post',
  ShowAddress: 'show-address',
  ReceiptRus: 'receipt-rus',
  ReceiptUsa: 'receipt-usa',
  ReceiptEur: 'receipt-eur',
  Quick: 'quick',
  Script: 'script',
} as const

type T_PaymentProcessType = typeof PAYMENT_PROCESS_TYPE[keyof typeof PAYMENT_PROCESS_TYPE]
type T_PaymentProcess = T_MoneyAccountPaymentRequestRes['process'] & { is_optional?: boolean }

const RECEIPT_PROCESS_TYPES: T_PaymentProcessType[] = [
  PAYMENT_PROCESS_TYPE.ReceiptRus,
  PAYMENT_PROCESS_TYPE.ReceiptUsa,
  PAYMENT_PROCESS_TYPE.ReceiptEur,
]

const AUTO_SUBMIT_PROCESS_TYPES: T_PaymentProcessType[] = [
  PAYMENT_PROCESS_TYPE.FormGet,
  PAYMENT_PROCESS_TYPE.FormPost,
  PAYMENT_PROCESS_TYPE.Script,
]

const DETAILS_BY_SBP_PROCESS_TYPES: T_PaymentProcessType[] = [
  ...AUTO_SUBMIT_PROCESS_TYPES,
  PAYMENT_PROCESS_TYPE.ShowAddress,
  PAYMENT_PROCESS_TYPE.Quick,
]
const SCRIPT_FLOW_PENDING_GUARD_TIMEOUT_MS = 4000
const NO_INTERNET_TOAST_MESSAGE = 'Нет подключения к интернету. Подключитесь, пожалуйста, к сети и попробуйте снова.'

const includesProcessType = (
  processType: string | undefined,
  processTypes: readonly T_PaymentProcessType[]
) => !!processType && (processTypes as readonly string[]).includes(processType)

const TOPUP_RETURN_PATH_RE = /^\/office\/topup\/[^/?#]+\/?$/
const SAFE_WINDOW_FN_PATH_RE = /^(?:window\.)?[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*$/

const openExternalInCordovaSystemBrowser = (url: string): boolean => {
  try {
    // Prefer explicit Cordova bridge for stable _system behavior.
    // @ts-expect-error cordova global
    if (typeof cordova !== 'undefined' && cordova?.InAppBrowser?.open) {
      // @ts-expect-error cordova global
      cordova.InAppBrowser.open(url, '_system')
      return true
    }

    const openedWindow = window.open(url, '_system')
    if (!openedWindow) {
      console.warn('[payment] window.open returned null for _system', { url })
      return false
    }
    console.warn('[payment] InAppBrowser unavailable, fallback to window.open(_system)')
    return true
  } catch (e) {
    console.warn('[payment] failed to open _system browser', e)
    return false
  }
}

const openCordovaPaymentWithBankAppSupport = (url: string): boolean => {
  const normalizedUrl = String(url || '').trim()
  if (!normalizedUrl) {
    return false
  }

  // For bank deep-links the most reliable option in Cordova is system browser,
  // because Android/iOS resolve installed banking apps there much better than WebView (_self).
  try {
    // @ts-expect-error cordova global
    if (typeof cordova !== 'undefined' && cordova?.InAppBrowser?.open) {
      // @ts-expect-error cordova global
      cordova.InAppBrowser.open(normalizedUrl, '_system')
      return true
    }
  } catch (e) {
    console.warn('[payment][cordova] failed to open payment in _system via InAppBrowser', e)
  }

  try {
    const openedWindow = window.open(normalizedUrl, '_system')
    if (openedWindow) return true
  } catch (e) {
    console.warn('[payment][cordova] failed to open payment via window.open(_system)', e)
  }

  try {
    // Last-resort fallback when _system is unavailable.
    window.location.assign(normalizedUrl)
    return true
  } catch (e) {
    console.warn('[payment][cordova] failed to navigate payment via window.location.assign fallback', e)
  }

  return false
}

const INVOICE_FIELD_ORDER = {
  company: [ 'name', 'account', 'address', 'inn', 'kpp', /* 'ogrn', */ 'nds' ],
  bank: [ 'name', 'bic', 'swift', 'account', 'address', 'branch', 'office' ],
  agent: [ 'name', 'address', 'swift' ],
} as const

const INVOICE_FIELD_LABELS: Record<string, Record<string, string>> = {
  company: {
    name: 'Получатель',
    account: 'Расчетный счет',
    address: 'Адрес',
    inn: 'ИНН',
    kpp: 'КПП',
    ogrn: 'ОГРН',
    nds: 'НДС',
  },
  bank: {
    name: 'Банк получателя',
    bic: 'БИК банка получателя',
    swift: 'SWIFT',
    account: 'Корр. счет',
    address: 'Адрес банка',
    branch: 'Филиал банка',
    office: 'Отделение банка',
  },
  agent: {
    name: 'Агент',
    address: 'Адрес агента',
    swift: 'SWIFT агента',
  },
}

const REQUIRED_INVOICE_FIELDS = [
  { group: 'company', field: 'name' },
  { group: 'company', field: 'account' },
  { group: 'company', field: 'inn' },
  { group: 'bank', field: 'name' },
  { group: 'bank', field: 'bic' },
] as const

export const TopUpBalanceDrawer: FC<T_TopUpBalanceDrawerProps> = ({
  onClose,
  isBuySubscription,
  initialAmount,
  onPayFromBalance,
  skipMethodSelection = false,
}) => {
  const userData = useAppStore((s) => s.userData)
  const isOnline = useOnlineStatus()
  const userBalance = Number(userData.mainAccount) || 0

  const [ step, setStep ] = useState<E_Steps>(
    skipMethodSelection
      ? E_Steps.AmountInput
      : E_Steps.MethodSelection
  )
  const [ amount, setAmount ] = useState<string>('')
  const [ paymentMethod, setPaymentMethod ] = useState<E_PaymentMethods>(E_PaymentMethods.BankTransfer)
  const [ topupAccounts, setTopupAccounts ] = useState<T_TopupAccount[]>([])
  const [ paymentOptions, setPaymentOptions ] = useState<T_TopupPaymentOption[]>([])
  const [ selectedPaymentOptionId, setSelectedPaymentOptionId ] = useState<number | null>(null)
  const [ selectedAccountPairId, setSelectedAccountPairId ] = useState<number | null>(null)
  const [ selectedCurrencyId, setSelectedCurrencyId ] = useState<number | null>(null)
  const [ paymentData, setPaymentData ] = useState<T_MoneyAccountPaymentRequestRes | null>(null)
  const [ paymentProcessMessage, setPaymentProcessMessage ] = useState<string | null>(null)
  const [ isLoading, setIsLoading ] = useState(false)

  const [ checkImage, setCheckImage ] = useState<string | null>(null)
  const [ isCheckSending, setIsCheckSending ] = useState(false)
  const [ availableRest, setAvailableRest ] = useState<number>(0)

  // Set initial amount if provided
  useEffect(() => {
    if (initialAmount) {
      setAmount(String(initialAmount))
    }
  }, [ initialAmount ])

  // Fetch available rest for Instant Payment
  useEffect(() => {
    const fetchAvailableRest = async () => {
      try {
        // AUX_API_URL removed - setting default value
        setAvailableRest(0)
      } catch (e) {
        console.error('Failed to fetch available rest:', e)
      }
    }

    fetchAvailableRest()
  }, [ userData.id, userData.shareholderStatusId ])

  const getInstantLimit = () => availableRest

  const getSelectedPaymentOption = () => {
    if (selectedPaymentOptionId === null) return null
    return paymentOptions.find((option) => option.id === selectedPaymentOptionId) || null
  }

  const parseAmountValue = (value: string): number | null => {
    const normalized = value.trim().replace(',', '.')
    if (!normalized) return null

    if (!/^(?:\d+|\d+\.\d{1,2}|0?\.\d{1,2})$/.test(normalized)) {
      return null
    }

    const parsed = Number(normalized)
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null
    }

    return parsed
  }

  const getCurrencyLabel = (currencyId: number | null) => {
    if (currencyId === 1) return 'RUB'
    if (currencyId === 2) return 'USD'
    if (currencyId === 3) return 'EUR'
    return currencyId ? `Валюта #${currencyId}` : ''
  }

  const getAmountCurrencyLabel = (currencyId: number | null) => {
    const currency = getCurrencyLabel(currencyId) || 'RUB'
    return currency === 'RUB' ? '₽' : currency
  }

  const getCurrencyTitleLabel = (currencyId: number | null) => {
    const currency = getCurrencyLabel(currencyId)
    return currency === 'RUB' ? 'Российский рубль' : (currency || '—')
  }

  const formatAmountWithCurrency = (
    value: string | number,
    withFraction = false
  ) => {
    const money = Number(value || 0).toLocaleString(
      'ru-RU',
      withFraction
        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        : undefined
    )

    return `${money} ${getAmountCurrencyLabel(selectedCurrencyId)}`
  }

  const getTopupAccountLine = (account: T_TopupAccount | undefined) => {
    if (!account) return '—'

    const shortTitle = account.title.replace(/\s*\([^)]*\)\s*$/, '')
    return `${shortTitle}: ${account.amount.format}`
  }

  const getNextStepByProcessType = (processType: string | undefined) => {
    if (includesProcessType(processType, RECEIPT_PROCESS_TYPES)) {
      return E_Steps.DetailsByBankDetails
    }

    if (includesProcessType(processType, DETAILS_BY_SBP_PROCESS_TYPES)) {
      return E_Steps.DetailsBySbp
    }

    return null
  }

  const getTrustedTopupCallbackUrl = () => {
    try {
      const isHttpProtocol = (protocol: string) => (
        protocol === 'https:' || protocol === 'http:'
      )
      const parseAbsoluteUrl = (value: string | null | undefined) => {
        if (!value) return null
        try {
          return new URL(value)
        } catch {
          return null
        }
      }
      const parseTrustedUrl = (value: string | null | undefined) => {
        const parsedUrl = parseAbsoluteUrl(value)
        return parsedUrl && isHttpProtocol(parsedUrl.protocol) ? parsedUrl : null
      }

      const trustedUrl = parseTrustedUrl(envs.TOPUP_CALLBACK_BASE_URL)
      const cordovaReturnBaseUrl = parseAbsoluteUrl(envs.CORDOVA_RETURN_BASE_URL)

      if (isCordovaApp && cordovaReturnBaseUrl) {
        // For auto-return into Cordova prefer dedicated return base (custom scheme or app-link host).
        const isWebLikeProtocol = isHttpProtocol(cordovaReturnBaseUrl.protocol)
        const callbackPath = isWebLikeProtocol
          ? '/office/topup/{request_id}?from_cordova=1'
          : '/topup/{request_id}?from_cordova=1'
        return new URL(callbackPath, cordovaReturnBaseUrl).toString()
      }

      if (!trustedUrl) return null

      let callbackOrigin = trustedUrl.origin

      // Prefer current host for callback ONLY when it is trusted.
      // This keeps return flow correct on stage/subdomains while avoiding open redirect to random origins.
      if (typeof window !== 'undefined') {
        const currentUrl = new URL(window.location.origin)
        const currentHost = currentUrl.hostname.toLowerCase()
        const trustedHost = trustedUrl.hostname.toLowerCase()
        const allowedCallbackRoots = envs.TOPUP_CALLBACK_ALLOWED_ROOTS || []

        const isSameHost = currentHost === trustedHost
        const isTrustedSubdomain = currentHost.endsWith(`.${trustedHost}`)
        const isAllowedCallbackHost = allowedCallbackRoots.some((allowedRoot) => (
          currentHost === allowedRoot || currentHost.endsWith(`.${allowedRoot}`)
        ))
        const isLocalDevHost = !envs.IS_PROD && (
          currentHost === 'localhost'
          || currentHost === '127.0.0.1'
        )

        if (
          isHttpProtocol(currentUrl.protocol)
          && (isSameHost || isTrustedSubdomain || isAllowedCallbackHost || isLocalDevHost)
        ) {
          callbackOrigin = currentUrl.origin
        }
      }

      return `${callbackOrigin}/office/topup/{request_id}`
    } catch {
      return null
    }
  }

  const getTopupRequestId = (data: T_MoneyAccountPaymentRequestRes | null | undefined): number | null => {
    const fromRootRequest = Number(data?.request)
    if (Number.isFinite(fromRootRequest) && fromRootRequest > 0) {
      return fromRootRequest
    }

    const fromProcessRequest = Number(data?.process?.data?.request?.id)
    if (Number.isFinite(fromProcessRequest) && fromProcessRequest > 0) {
      return fromProcessRequest
    }

    return null
  }

  const ensureTopupAccounts = useCallback(async (): Promise<{ accountPairId: number, currencyId: number } | null> => {
    if (selectedAccountPairId && selectedCurrencyId) {
      return {
        accountPairId: selectedAccountPairId,
        currencyId: selectedCurrencyId,
      }
    }

    if (!isOnline) {
      toast.error(NO_INTERNET_TOAST_MESSAGE)
      return null
    }

    setIsLoading(true)

    try {
      const balanceRes = await apiRequest<T_UniRes<T_UserBalanceRes>>({
        method: 'money/account/balance',
        param: {},
      })

      if (balanceRes.code !== 'ok') {
        showNoInternetToast()
        return null
      }

      const availableTopupAccounts = balanceRes.data.list.filter((item) => item.is_topup)

      if (!availableTopupAccounts.length) {
        toast.error('Не найден счет для пополнения')
        return null
      }

      const defaultTopupAccount = availableTopupAccounts.find((item) => item.currency === 1)
        || availableTopupAccounts[0]

      setTopupAccounts(availableTopupAccounts)
      setSelectedAccountPairId(defaultTopupAccount.id)
      setSelectedCurrencyId(defaultTopupAccount.currency)

      return {
        accountPairId: defaultTopupAccount.id,
        currencyId: defaultTopupAccount.currency,
      }
    } catch (e) {
      console.error(e)
      showNoInternetToast()
      return null
    } finally {
      setIsLoading(false)
    }
  }, [ selectedAccountPairId, selectedCurrencyId, isOnline ])

  useEffect(() => {
    if (
      step === E_Steps.AmountInput
      && paymentMethod === E_PaymentMethods.BankTransfer
      && topupAccounts.length === 0
    ) {
      void ensureTopupAccounts()
    }
  }, [ step, paymentMethod, topupAccounts.length, ensureTopupAccounts ])

  const hasInternetConnection = () => (
    isOnline
  )

  const showNoInternetToast = () => {
    toast.error(NO_INTERNET_TOAST_MESSAGE)
  }

  const loadPaymentOptions = async (validatedAmount: number) => {
    if (!Number.isFinite(validatedAmount) || validatedAmount <= 0) {
      toast.error('Введите корректную сумму')
      return false
    }

    if (!hasInternetConnection()) {
      showNoInternetToast()
      return false
    }

    const accountData = await ensureTopupAccounts()

    if (!accountData) return false

    setIsLoading(true)

    try {
      const methodsRes = await apiRequest<T_UniRes<T_TopupPaymentMethodsRes>>({
        method: 'money/account/payment/methods',
        param: {
          value: String(validatedAmount),
          account_pair: accountData.accountPairId,
        },
      })

      if (methodsRes.code !== 'ok') {
        showNoInternetToast()
        return false
      }

      const externalOptions = sortPaymentOptionsCardsFirst(methodsRes.data.external || [])

      if (!externalOptions.length) {
        toast.error('Способы оплаты недоступны')
        return false
      }

      setPaymentOptions(externalOptions)
      setSelectedPaymentOptionId((prev) => {
        if (prev !== null && externalOptions.some((option) => option.id === prev)) {
          return prev
        }

        const cardOption = externalOptions.find(isCardPaymentOption)
        return (cardOption || externalOptions[0]).id
      })

      return true
    } catch (e) {
      console.error(e)
      showNoInternetToast()
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const getPaymentRequestValidationError = (): string | null => {
    const validatedAmount = parseAmountValue(amount)
    if (validatedAmount === null) {
      return 'Введите корректную сумму'
    }

    const selectedOption = getSelectedPaymentOption()
    if (!selectedOption) {
      return 'Выберите способ оплаты'
    }

    if (!selectedAccountPairId) {
      return 'Не найден счет для пополнения'
    }

    if (!selectedCurrencyId) {
      return 'Не найдена валюта пополнения'
    }

    return null
  }

  const handleNext = async () => {
    if (step === E_Steps.MethodSelection) {
      setStep(E_Steps.AmountInput)
      return
    }

    if (step === E_Steps.PaymentTypeSelection) {
      setStep(E_Steps.PaymentConfirm)
      return
    }

    if (step === E_Steps.PaymentConfirm) {
      if (!hasInternetConnection()) {
        showNoInternetToast()
        return
      }

      const validationError = getPaymentRequestValidationError()
      if (validationError) {
        toast.error(validationError)
        return
      }

      await handleCreatePaymentRequest()
      return
    }

    const validatedAmount = parseAmountValue(amount)
    if (validatedAmount === null) {
      toast.error('Введите корректную сумму')
      return
    }

    // Instant Credit Logic
    if (isInstantPayment) {
      const limit = getInstantLimit()

      if (validatedAmount > limit) {
        toast.error(`Для вашего статуса максимальная сумма мгновенного пополнения: ${limit} ₽`)
        return
      }

      if (!checkImage) {
        toast.error('Пожалуйста, прикрепите чек')
        return
      }

      await handleSendCheck()
      return
    }

    if (isBuySubscription && isInternalPayment) {
      if (!onPayFromBalance) return
      setIsLoading(true)

      try {
        await onPayFromBalance()
        onClose()
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }

      return
    }

    if (!isInternalPayment && !isInstantPayment) {
      const isMethodsLoaded = await loadPaymentOptions(validatedAmount)
      if (isMethodsLoaded) {
        setStep(E_Steps.PaymentTypeSelection)
      }
      return
    }
  }

  const handleCreatePaymentRequest = async () => {
    const validationError = getPaymentRequestValidationError()
    if (validationError) {
      toast.error(validationError)
      return
    }

    const validatedAmount = parseAmountValue(amount)
    const selectedOption = getSelectedPaymentOption()

    if (validatedAmount === null || !selectedOption || !selectedAccountPairId || !selectedCurrencyId) {
      toast.error('Не удалось подготовить данные платежа')
      return
    }

    const callbackUrl = getTrustedTopupCallbackUrl()

    if (!callbackUrl) {
      toast.error('Не настроен доверенный URL возврата после оплаты')
      return
    }

    setPaymentProcessMessage(null)
    setIsLoading(true)

    try {

      const res = await apiRequest<T_UniRes<T_MoneyAccountPaymentRequestRes>>({
        method: 'money/account/payment/request',
        param: {
          currency: selectedCurrencyId,
          id: selectedOption.id,
          account_type: selectedAccountPairId,
          value: String(validatedAmount),
          visitor_error_url: callbackUrl,
          visitor_success_url: callbackUrl,
          visitor_screen_width: window.outerWidth,
          visitor_screen_height: window.outerHeight,
        }
      })

      if (res.code === 'ok') {
        const processType = res.data.process?.type
        const processMessage = normalizeProcessMessage(res.message)
        const nextStep = getNextStepByProcessType(res.data.process?.type)

        if (!nextStep) {
          toast.error('Неизвестный сценарий оплаты')
          return
        }

        if (nextStep === E_Steps.DetailsByBankDetails) {
          const processData = res.data.process?.data

          if (!processData || typeof processData !== 'object' || Array.isArray(processData)) {
            toast.error('Платежная система вернула пустые реквизиты')
            return
          }

          const missingInvoiceFields = getMissingRequiredInvoiceFields(processData)

          if (missingInvoiceFields.length > 0) {
            toast.error(`Реквизиты получены не полностью: ${missingInvoiceFields.join(', ')}`)
            return
          }
        }

        if (nextStep === E_Steps.DetailsBySbp && includesProcessType(processType, AUTO_SUBMIT_PROCESS_TYPES)) {
          await runExternalPaymentProcess(res.data.process, getTopupRequestId(res.data))
          return
        }

        setPaymentProcessMessage(nextStep === E_Steps.DetailsBySbp ? processMessage : null)
        setPaymentData(res.data)
        setStep(nextStep)
      } else {
        toast.error(res.message || 'Ошибка при создании заявки')
      }
    } catch (e) {
      console.error(e)
      toast.error('Ошибка при создании заявки')
    } finally {
      setIsLoading(false)
    }
  }

  const getProcessDataAsRecord = (data: unknown): Record<string, unknown> => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return {}
    }

    return data as Record<string, unknown>
  }

  const normalizeProcessMessage = (value: unknown): string | null => {
    if (typeof value !== 'string') {
      return null
    }

    const normalizedValue = value.trim()

    if (!normalizedValue || normalizedValue.toLowerCase() === 'ok') {
      return null
    }

    return normalizedValue
  }

  const getProcessFieldValue = (
    processData: unknown,
    groupKey: string,
    fieldKey: string
  ): string | null => {
    const data = getProcessDataAsRecord(processData)
    const rawGroup = data[groupKey]

    if (!rawGroup || typeof rawGroup !== 'object' || Array.isArray(rawGroup)) {
      return null
    }

    const rawValue = (rawGroup as Record<string, unknown>)[fieldKey]

    if (rawValue === null || rawValue === undefined) {
      return null
    }

    const normalizedValue = String(rawValue).trim()
    return normalizedValue || null
  }

  const getMissingRequiredInvoiceFields = (processData: unknown) => (
    REQUIRED_INVOICE_FIELDS
      .filter(({ group, field }) => !getProcessFieldValue(processData, group, field))
      .map(({ group, field }) => (
        INVOICE_FIELD_LABELS[group]?.[field]
        || `${group}.${field}`
      ))
  )

  const getReceiptQrContent = (processData: unknown): string | null => {
    const data = getProcessDataAsRecord(processData)
    const qrcode = data.qrcode

    if (!qrcode || typeof qrcode !== 'object' || Array.isArray(qrcode)) {
      return null
    }

    const rawContent = (qrcode as Record<string, unknown>).content

    if (typeof rawContent !== 'string') {
      return null
    }

    const normalizedContent = rawContent.trim()
    return normalizedContent || null
  }

  const getProcessRootFieldValue = (
    processData: unknown,
    fieldKey: string
  ): string | null => {
    const data = getProcessDataAsRecord(processData)
    const rawValue = data[fieldKey]

    if (rawValue === null || rawValue === undefined) {
      return null
    }

    const normalizedValue = String(rawValue).trim()
    return normalizedValue || null
  }

  const getInvoiceDetails = (processData: unknown) => {
    const data = getProcessDataAsRecord(processData)
    const details: Array<{ label: string, value: string }> = []

    const noteValue = typeof data.note === 'string'
      ? data.note.trim()
      : ''

    details.push({
      label: 'Назначение платежа',
      value: noteValue || 'Паевый взнос',
    })

    ;(Object.keys(INVOICE_FIELD_ORDER) as Array<keyof typeof INVOICE_FIELD_ORDER>)
      .forEach((groupKey) => {
        const rawGroup = data[groupKey]
        if (!rawGroup || typeof rawGroup !== 'object' || Array.isArray(rawGroup)) {
          return
        }

        const groupFields = INVOICE_FIELD_ORDER[groupKey]
        const groupLabels = INVOICE_FIELD_LABELS[groupKey] || {}
        const groupRecord = rawGroup as Record<string, unknown>

        groupFields.forEach((fieldKey) => {
          const rawValue = groupRecord[fieldKey]
          if (rawValue === null || rawValue === undefined) return

          const value = String(rawValue).trim()
          if (!value) return

          details.push({
            label: groupLabels[fieldKey] || fieldKey,
            value,
          })
        })
      })

    return details
  }

  const handleFormGetPaymentRequest = (process: T_PaymentProcess): boolean => {
    const url = new URL(process.link, window.location.origin)
    const params = getProcessDataAsRecord(process.data)

    Object.entries(params).forEach(([ key, value ]) => {
      url.searchParams.set(key, String(value))
    })

    if (process.is_optional) {
      return true
    }

    if (isCordovaApp) {
      return openCordovaPaymentWithBankAppSupport(url.toString())
    }

    window.location.assign(url.toString())
    return true
  }

  const handleFormPostPaymentRequest = (process: T_PaymentProcess) => {
    const form = document.createElement('form')
    const params = getProcessDataAsRecord(process.data)

    form.setAttribute('method', 'POST')
    form.setAttribute('action', process.link)
    form.setAttribute('target', '_self')
    form.style.display = 'none'

    Object.entries(params).forEach(([ key, value ]) => {
      const input = document.createElement('input')
      input.setAttribute('name', key)
      input.setAttribute('value', String(value))
      input.setAttribute('type', 'hidden')
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
  }

  const handleScriptPaymentRequest = async (process: T_PaymentProcess) => {
    const tryInvokeSafeScriptClick = (clickValue: unknown): boolean => {
      if (typeof clickValue !== 'string') return true

      const rawClick = clickValue.trim()
      if (!rawClick) return true

      const normalizedFnPath = rawClick.endsWith('()')
        ? rawClick.slice(0, -2).trim()
        : rawClick

      if (SAFE_WINDOW_FN_PATH_RE.test(normalizedFnPath)) {
        const pathWithoutWindow = normalizedFnPath.replace(/^window\./, '')
        const keys = pathWithoutWindow.split('.')
        let target: any = window

        for (const key of keys) {
          if (!target || typeof target !== 'object' || !(key in target)) {
            target = null
            break
          }
          target = target[key]
        }

        if (typeof target === 'function') {
          try {
            target(() => {})
            return true
          } catch {
            return false
          }
        }
      }

      const selector = rawClick.startsWith('selector:')
        ? rawClick.slice('selector:'.length).trim()
        : rawClick

      const isSelectorCandidate = selector.startsWith('#') || selector.startsWith('.') || selector.startsWith('[')
      if (!isSelectorCandidate || /[<>{};]/.test(selector)) {
        return false
      }

      try {
        const node = document.querySelector<HTMLElement>(selector)
        if (!node) return false
        node.click()
        return true
      } catch {
        return false
      }
    }

    const scriptData = getProcessDataAsRecord(process.data)
    const loadItems = Array.isArray(scriptData.load) ? scriptData.load : []

    await Promise.all(loadItems.map((item: any) => (
      new Promise<void>((resolve, reject) => {
        const script = document.createElement('script')

        if (item.src) script.src = item.src
        if (item.className) script.className = item.className
        if (item.dataset && typeof item.dataset === 'object') {
          Object.entries(item.dataset).forEach(([ key, value ]) => {
            script.dataset[key] = String(value)
          })
        }
        if (item.attrs && typeof item.attrs === 'object') {
          Object.entries(item.attrs).forEach(([ key, value ]) => {
            script.setAttribute(key, String(value))
          })
        }

        script.onload = () => resolve()
        script.onerror = () => reject(new Error(`Не удалось загрузить скрипт: ${item.src || 'unknown'}`))

        document.body.appendChild(script)
      })
    )))

    const loadEvent = new Event('load')
    window.dispatchEvent(loadEvent)

    if (!tryInvokeSafeScriptClick(scriptData.click)) {
      console.warn('[payment][script] blocked unsafe click payload', scriptData.click)
      toast.error('Сценарий запуска оплаты отклонен политикой безопасности')
      return false
    }

    return true
  }

  const waitForExternalFlowStart = async (timeoutMs = SCRIPT_FLOW_PENDING_GUARD_TIMEOUT_MS) => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return false
    }

    if (document.visibilityState === 'hidden') {
      return true
    }

    return await new Promise<boolean>((resolve) => {
      let isSettled = false
      let timeoutId: number | null = null

      const finalize = (result: boolean) => {
        if (isSettled) return
        isSettled = true
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId)
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        window.removeEventListener('pagehide', handlePageHide)
        resolve(result)
      }

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          finalize(true)
        }
      }
      const handlePageHide = () => finalize(true)

      document.addEventListener('visibilitychange', handleVisibilityChange)
      window.addEventListener('pagehide', handlePageHide)

      timeoutId = window.setTimeout(() => finalize(false), timeoutMs)
    })
  }

  const getPendingTopupBaseline = (): T_TopupBalancePendingBaseline | null => {
    const selectedTopupAccount = topupAccounts.find((account) => (
      account.id === selectedAccountPairId
    ))

    if (!selectedTopupAccount) {
      return null
    }

    return {
      accountId: selectedTopupAccount.id,
      currencyId: Number(selectedTopupAccount.currency) || null,
      amountValue: Number(selectedTopupAccount.amount?.value) || 0,
      blockedValue: Number(selectedTopupAccount.blocked?.value) || 0,
      capturedAt: Date.now(),
    }
  }

  const runExternalPaymentProcess = async (
    process: T_PaymentProcess | null | undefined,
    requestId: number | null = null,
  ) => {
    if (!process) {
      toast.error('Платежные данные не найдены')
      return
    }

    try {
      setIsLoading(true)
      const baseline = getPendingTopupBaseline()

      if (process.type === PAYMENT_PROCESS_TYPE.FormGet) {
        if (!process.is_optional) {
          setTopupBalancePendingSession({ requestId, baseline })
        }

        const didOpenExternalFlow = handleFormGetPaymentRequest(process)
        if (!didOpenExternalFlow) {
          console.warn('[payment][form-get] failed to start external flow, continue with pending UX')
        }

        if (isCordovaApp && !process.is_optional) {
          handleClose()
        }
        return
      }

      if (process.type === PAYMENT_PROCESS_TYPE.FormPost) {
        const isPendingStored = setTopupBalancePendingSession({ requestId, baseline })
        handleFormPostPaymentRequest(process)

        if (isCordovaApp) {
          if (!isPendingStored) {
            console.warn('[payment][form-post] pending session not stored before close', {
              requestId,
            })
          }
          handleClose()
        }

        return
      }

      if (process.type === PAYMENT_PROCESS_TYPE.Script) {
        const hasStarted = await handleScriptPaymentRequest(process)
        if (hasStarted) {
          const isPendingStored = setTopupBalancePendingSession({ requestId, baseline })
          if (isPendingStored) {
            const didLeaveCurrentContext = await waitForExternalFlowStart()
            if (!didLeaveCurrentContext) {
              clearTopupBalancePendingSession()
            }
          }
        }
        return
      }

      toast.error('Для этого способа нет сценария отправки')
    } catch (e) {
      console.error(e)
      toast.error('Ошибка при запуске оплаты')
    } finally {
      setIsLoading(false)
    }
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`${label} скопировано`)
  }

  const getPrintReceiptUrl = () => {
    const printLink = paymentData?.process?.print

    const normalizeUrl = (value: string) => {
      try {
        return new URL(value, envs.API_DOMAIN || window.location.origin)
      } catch {
        return null
      }
    }

    if (printLink) {
      const normalizedPrintUrl = normalizeUrl(printLink)

      if (normalizedPrintUrl) {
        // Иногда backend возвращает callback (/office/topup/{request_id}) вместо print URL.
        if (!TOPUP_RETURN_PATH_RE.test(normalizedPrintUrl.pathname)) {
          return normalizedPrintUrl.toString()
        }
      }
    }

    const processType = paymentData?.process?.type
    const requestId = paymentData?.process?.data?.request?.id || paymentData?.request
    const isReceiptProcess = (
      processType === PAYMENT_PROCESS_TYPE.ReceiptRus
      || processType === PAYMENT_PROCESS_TYPE.ReceiptUsa
      || processType === PAYMENT_PROCESS_TYPE.ReceiptEur
    )

    if (!isReceiptProcess || !requestId) {
      return null
    }

    return normalizeUrl(
      `/do.control/printpay/bank?hlf/request=${encodeURIComponent(String(requestId))}`
    )?.toString() || null
  }

  const handlePrintReceipt = () => {
    const printUrl = getPrintReceiptUrl()

    if (!printUrl) {
      toast.error('Ссылка на квитанцию недоступна')
      return
    }

    if (isCordovaApp) {
      const didOpen = openExternalInCordovaSystemBrowser(printUrl)
      if (!didOpen) {
        toast.error('Не удалось открыть ссылку в браузере')
      }
      return
    }

    window.open(printUrl, '_blank', 'noopener,noreferrer')
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onloadend = () => {
      setCheckImage(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleSendCheck = async () => {
    if (!checkImage) return

    setIsCheckSending(true)

    try {
      // AUX_API_URL removed - check creation disabled
      throw new Error('Check creation is not available')
    } catch (e) {
      console.error(e)
      toast.error('Ошибка при отправке чека')
    } finally {
      setIsCheckSending(false)
    }
  }

  const handleClose = () => {
    onClose()
    setTimeout(() => {
      setStep(skipMethodSelection ? E_Steps.AmountInput : E_Steps.MethodSelection)
      setPaymentData(null)
      setPaymentProcessMessage(null)
      setAmount('')
      setPaymentMethod(E_PaymentMethods.BankTransfer)
      setTopupAccounts([])
      setPaymentOptions([])
      setSelectedPaymentOptionId(null)
      setSelectedAccountPairId(null)
      setSelectedCurrencyId(null)
      setCheckImage(null)
    }, 300)
  }

  const isInternalPayment = paymentMethod === E_PaymentMethods.InternalAccount
  const isInstantPayment = paymentMethod === E_PaymentMethods.Instant
  const selectedPaymentOption = getSelectedPaymentOption()
  const paymentRequestValidationError = getPaymentRequestValidationError()
  const isTopupAccountSelectionVisible = !isInstantPayment && !isInternalPayment
  const selectedTopupAccount = topupAccounts.find((account) => account.id === selectedAccountPairId)
  const processQrContent = getReceiptQrContent(paymentData?.process?.data)
  const detailsBySbpProcessType = paymentData?.process?.type
  const isQuickResultProcess = detailsBySbpProcessType === PAYMENT_PROCESS_TYPE.Quick
  const isShowAddressResultProcess = detailsBySbpProcessType === PAYMENT_PROCESS_TYPE.ShowAddress
  const quickAccountValue = getProcessRootFieldValue(paymentData?.process?.data, 'account')
  const quickNoteValue = getProcessRootFieldValue(paymentData?.process?.data, 'note')
  const cryptoModeValue = getProcessRootFieldValue(paymentData?.process?.data, 'mode')
  const cryptoAddressValue = getProcessRootFieldValue(paymentData?.process?.data, 'address')
  const cryptoNoteValue = getProcessRootFieldValue(paymentData?.process?.data, 'note')
  const detailsBySbpWarningFallback = isQuickResultProcess
    ? 'Проверьте реквизиты и выполните оплату. Пополнение баланса произойдет после подтверждения платежной системой.'
    : isShowAddressResultProcess
      ? 'Используйте указанные реквизиты для оплаты. Зачисление произойдет после подтверждения платежной системой.'
      : null
  const detailsBySbpWarningContent = paymentProcessMessage || detailsBySbpWarningFallback


  return (
    <Drawer
      onClose={handleClose}
      backTitle={
        step > E_Steps.MethodSelection
          ? 'Назад'
          : undefined
      }
      onBackClick={
        step > E_Steps.MethodSelection
          ? () => {
            if (skipMethodSelection && step === E_Steps.AmountInput) {
              handleClose()
              return
            }

            if (step === E_Steps.DetailsBySbp || step === E_Steps.DetailsByBankDetails) {
              setStep(E_Steps.PaymentTypeSelection)
              return
            }

            setStep((s) => s - 1 as E_Steps)
          }
          : undefined
      }
      isBackdropClosable
      isFullScreen
    >
      <SC_Content>
        {/* Шаг 1 */}
        {step === E_Steps.MethodSelection && (
          <Flex vertical gap={20}>
            <Heading level={2}>
              Выберите способ
            </Heading>

            <Radio.Group
              onChange={(e) => setPaymentMethod(e.target.value)}
              value={paymentMethod}
            >
              <Flex vertical gap={10}>
                <Radio
                  value={E_PaymentMethods.Instant}
                  disabled={!accountsForTesting.includes(userData.id || 0)}
                  // disabled={availableRest <= 0}
                >
                  Внесение паевого взноса моментальное {
                    availableRest > 0
                      ? `(до ${formatMoney(getInstantLimit())})`
                      : '(лимит исчерпан)'
                  }
                </Radio>

                <Radio value={E_PaymentMethods.BankTransfer}>
                  Внесение паевого взноса с подтверждением банковского перевода (без ограничения по сумме)
                </Radio>

                {isBuySubscription && (
                  <Radio
                    value={E_PaymentMethods.InternalAccount}
                    disabled={userBalance < (initialAmount || 0)}
                  >
                    с внутреннего счета ({formatMoney(userBalance)})
                  </Radio>
                )}
              </Flex>
            </Radio.Group>

            <Button type='primary' onClick={handleNext} block>
              Далее
            </Button>

            <Button onClick={handleClose} block>
              Отмена
            </Button>
          </Flex>
        )}

        {/* Шаг 2 */}
        {step === E_Steps.AmountInput && (
          <Flex vertical gap={20}>
            <Heading level={2}>
              {isBuySubscription ? 'Покупка тарифа' : 'Пополнение баланса'}
            </Heading>

            <Input
              label='Сумма пополнения'
              value={amount}
              onChange={(e) => {
                const val = e.target.value
                if (/^\d*\.?\d*$/.test(val)) {
                  setAmount(val)
                }
              }}
              placeholder='Введите сумму'
              $customType='numeric'
              readOnly={!!initialAmount}
              $isWhite
            />

            {isTopupAccountSelectionVisible && (
              <Flex vertical gap={10} style={{ opacity: 0.65 }}>
                <Flex vertical gap={8}>
                  <span style={{ color: '#8c8c8c', fontSize: 14 }}>Счет пополнения</span>
                  <span style={{ fontSize: 16 }}>
                    {getTopupAccountLine(selectedTopupAccount)}
                  </span>
                  <span style={{ fontSize: 16 }}>
                    {`Валюта: ${getCurrencyTitleLabel(selectedCurrencyId)}`}
                  </span>
                </Flex>
              </Flex>
            )}

            {isInstantPayment && <>
              <p>Прикрепите скриншот чека об оплате:</p>

              {!checkImage ? (
                <Button onClick={() => document.getElementById('check-upload')?.click()}>
                  Загрузить чек
                </Button>
              ) : (
                <Flex vertical gap={10}>
                  <img
                    src={checkImage}
                    alt='Check'
                    style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                  />

                  <Button
                    onClick={() => document.getElementById('check-upload')?.click()}
                    disabled={isCheckSending}
                  >
                    Изменить
                  </Button>
                </Flex>
              )}
            </>}

            <Button
              type='primary'
              onClick={handleNext}
              block
              loading={isLoading || isCheckSending}
              disabled={
                !amount
                || (isInstantPayment ? !checkImage : false)
                || isCheckSending
              }
            >
              {isInstantPayment
                ? 'Отправить чек'
                : 'Далее'
              }
            </Button>
          </Flex>
        )}

        {/* Шаг 3 */}
        {step === E_Steps.PaymentTypeSelection && (
          <Flex vertical gap={20}>
            <Heading level={2}>
              Способ оплаты
            </Heading>

            <Radio.Group
              onChange={(e) => setSelectedPaymentOptionId(Number(e.target.value))}
              value={selectedPaymentOptionId ?? undefined}
            >
              <Flex vertical gap={32}>
                {paymentOptions.map((option) => {
                  const creditingTime = getPaymentCreditingTime(option)

                  return (
                    <Radio key={option.id} value={option.id}>
                      <Flex vertical gap={4}>
                        <span>{option.title}</span>
                        <span>Сумма: {formatAmountWithCurrency(amount)}</span>
                        <span>Без комиссии</span>
                        {creditingTime && (
                          <SC_CreditingTime $variant={creditingTime.variant}>
                            {creditingTime.text}
                          </SC_CreditingTime>
                        )}
                      </Flex>
                    </Radio>
                  )
                })}
              </Flex>
            </Radio.Group>

            <Button
              type='primary'
              onClick={handleNext}
              block
              loading={isLoading}
              disabled={!selectedPaymentOptionId}
              style={{ marginTop: 16 }}
            >
              Далее
            </Button>
          </Flex>
        )}

        {/* Шаг 4 */}
        {step === E_Steps.PaymentConfirm && (
          <Flex vertical gap={20}>
            <Heading level={2}>
              Подтверждение
            </Heading>

            <Heading level={3}>
              {selectedPaymentOption?.title || 'Способ оплаты'}
            </Heading>

            <Flex vertical gap={4}>
              <span style={{ color: '#8c8c8c', fontSize: 14 }}>Сумма пополнения</span>
              <span style={{ fontSize: 18, color: '#8c8c8c' }}>
                {formatAmountWithCurrency(amount, true)}
              </span>
            </Flex>

            <div style={{ borderBottom: '1px dotted #ccc' }} />

            <fieldset style={{
              border: '2px solid #333',
              borderRadius: '4px',
              padding: '8px 12px 12px 12px',
              margin: 0
            }}>
              <legend style={{
                fontSize: '14px',
                color: '#555',
                padding: '0 4px',
                lineHeight: 1
              }}>
                Сумма для оплаты
              </legend>
              <div style={{ fontSize: '16px', color: '#333' }}>
                {formatAmountWithCurrency(amount, true)}
              </div>
            </fieldset>

            <p style={{ color: '#8c8c8c', fontSize: 14 }}>
              * Пополнение лицевого счета произойдет после подтверждения платежной системой.
            </p>

            <Button
              type='primary'
              block
              onClick={handleNext}
              loading={isLoading}
              disabled={!!paymentRequestValidationError || isLoading}
            >
              ОТПРАВИТЬ
            </Button>
          </Flex>
        )}

        {/* Шаг 5 */}
        {step === E_Steps.DetailsByBankDetails && paymentData && (
          <Flex vertical gap={20}>
            {(() => {
              const printReceiptUrl = getPrintReceiptUrl()

              return (
                <Flex justify='space-between' align='flex-start' gap={12}>
                  <Flex vertical gap={14}>
                    <Heading level={2}>
                      Сумма пополнения
                    </Heading>

                    <Heading level={1}>
                      {formatAmountWithCurrency(amount)}
                    </Heading>
                  </Flex>

                  <Tooltip title='Распечатать квитанцию'>
                    <Button
                      type='text'
                      aria-label='Распечатать квитанцию'
                      icon={<PrinterOutlined style={{ fontSize: 22 }} />}
                      onClick={handlePrintReceipt}
                      disabled={!printReceiptUrl}
                    />
                  </Tooltip>
                </Flex>
              )
            })()}

            <p>
              Для пополнения баланса переведите нужную сумму по реквизитам ниже. Можно также отсканировать QR-код в вашем банковском приложении.
            </p>

            <p>
              Если эта страница открыта у вас на мобильном устройстве с которого вы будете оплачивать, то можно сделать скриншот и загрузить его прямо в банковское приложение. Если же она открыта с другого устройства, то воспользуйтесь сканером QR внтутри вашего банковского приложения, чтобы не вводить всё вручную.
            </p>

            {processQrContent
              ? (
                <Popover
                  content={
                    <div style={{ padding: 10, background: 'white' }}>
                      <QRCodeCP1251
                        value={processQrContent}
                        size={256}
                      />
                    </div>
                  }
                  trigger='click'
                >
                  <Button type='primary' block>
                    Показать QR-код для оплаты
                  </Button>
                </Popover>
              )
              : (
                <p style={{ color: '#8c8c8c', margin: 0 }}>
                  QR-код не предоставлен платежной системой. Используйте реквизиты ниже.
                </p>
              )}

            <br />

            <Heading level={2}>
              Реквизиты для перевода
            </Heading>

            <Flex vertical gap={10}>
              {getInvoiceDetails(paymentData?.process?.data).map((item, index) => (
                <SC_FieldRow key={`${item.label}-${index}`}>
                  <SC_FieldLabel>
                    {item.label}
                  </SC_FieldLabel>

                  <Input
                    value={item.value}
                    readOnly
                    suffix={
                      <Button
                        type='text'
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(item.value, item.label)}
                      />
                    }
                  />
                </SC_FieldRow>
              ))}
            </Flex>
          </Flex>
        )}

        {/* Шаг 6 */}
        {step === E_Steps.DetailsBySbp && paymentData && (
          <Flex vertical gap={20}>
            <Heading level={2}>
              {selectedPaymentOption?.title || 'Результат оплаты'}
            </Heading>

            <Flex vertical gap={4}>
              <span style={{ color: '#8c8c8c', fontSize: 14 }}>Сумма пополнения</span>
              <span style={{ fontSize: 18, color: '#8c8c8c' }}>
                {formatAmountWithCurrency(amount, true)}
              </span>
            </Flex>

            {detailsBySbpWarningContent && (
              <div style={{
                border: '1px solid #d9e9ff',
                background: '#f5f9ff',
                borderRadius: 8,
                padding: '10px 12px'
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  Внимание
                </div>

                {paymentProcessMessage
                  ? (
                    <div
                      style={{ margin: 0, fontSize: 14, color: '#3d3d3d' }}
                      dangerouslySetInnerHTML={{ __html: detailsBySbpWarningContent }}
                    />
                  )
                  : (
                    <p style={{ margin: 0, fontSize: 14, color: '#3d3d3d' }}>
                      {detailsBySbpWarningContent}
                    </p>
                  )}
              </div>
            )}

            {isQuickResultProcess && (
              <Flex vertical gap={10}>
                {quickAccountValue && (
                  <SC_FieldRow>
                    <SC_FieldLabel>
                      Счет для перевода
                    </SC_FieldLabel>

                    <Input
                      value={quickAccountValue}
                      readOnly
                      suffix={
                        <Button
                          type='text'
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(quickAccountValue, 'Счет для перевода')}
                        />
                      }
                    />
                  </SC_FieldRow>
                )}

                {quickNoteValue && (
                  <SC_FieldRow>
                    <SC_FieldLabel>
                      Назначение платежа
                    </SC_FieldLabel>

                    <Input
                      value={quickNoteValue}
                      readOnly
                      suffix={
                        <Button
                          type='text'
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(quickNoteValue, 'Назначение платежа')}
                        />
                      }
                    />
                  </SC_FieldRow>
                )}

                {!quickAccountValue && !quickNoteValue && (
                  <p style={{ color: '#8c8c8c', margin: 0 }}>
                    Платежные реквизиты не предоставлены платежной системой.
                  </p>
                )}
              </Flex>
            )}

            {isShowAddressResultProcess && (
              <Flex vertical gap={10}>
                {cryptoModeValue && (
                  <SC_FieldRow>
                    <SC_FieldLabel>
                      Режим
                    </SC_FieldLabel>

                    <Input
                      value={cryptoModeValue}
                      readOnly
                      suffix={
                        <Button
                          type='text'
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(cryptoModeValue, 'Режим')}
                        />
                      }
                    />
                  </SC_FieldRow>
                )}

                {cryptoAddressValue && (
                  <SC_FieldRow>
                    <SC_FieldLabel>
                      Адрес
                    </SC_FieldLabel>

                    <Input
                      value={cryptoAddressValue}
                      readOnly
                      suffix={
                        <Button
                          type='text'
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(cryptoAddressValue, 'Адрес')}
                        />
                      }
                    />
                  </SC_FieldRow>
                )}

                {cryptoNoteValue && (
                  <SC_FieldRow>
                    <SC_FieldLabel>
                      Примечание
                    </SC_FieldLabel>

                    <Input
                      value={cryptoNoteValue}
                      readOnly
                      suffix={
                        <Button
                          type='text'
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(cryptoNoteValue, 'Примечание')}
                        />
                      }
                    />
                  </SC_FieldRow>
                )}

                {processQrContent
                  ? (
                    <Popover
                      content={
                        <div style={{ padding: 10, background: 'white' }}>
                          <QRCodeCP1251
                            value={processQrContent}
                            size={256}
                          />
                        </div>
                      }
                      trigger='click'
                    >
                      <Button type='primary' block>
                        Показать QR-код
                      </Button>
                    </Popover>
                  )
                  : null}

                {!cryptoModeValue && !cryptoAddressValue && !cryptoNoteValue && !processQrContent && (
                  <p style={{ color: '#8c8c8c', margin: 0 }}>
                    Данные для оплаты не предоставлены платежной системой.
                  </p>
                )}
              </Flex>
            )}

            {!isQuickResultProcess && !isShowAddressResultProcess && (
              <p style={{ color: '#8c8c8c', margin: 0 }}>
                Для этого способа нет экрана результата.
              </p>
            )}

            <Button block onClick={handleClose}>
              Закрыть
            </Button>
          </Flex>
        )}

        <input
          type='file'
          id='check-upload'
          style={{ display: 'none' }}
          accept='image/*'
          onChange={handleFileChange}
        />
      </SC_Content>
    </Drawer>
  )
}
